import { useFlowSessionById } from "@/api/flow_sessions";
import type {
  CanvasChatSessionNodeData,
  CanvasNodeProps,
} from "@/pages/canvas/types/canvasNodeData";
import { cn } from "@/utils";
import { ViewportPortal, useViewport } from "@xyflow/react";
import { MessageSquare } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { memo, useCallback, useMemo, useRef } from "react";
import { ChatPanel, PANEL_HEIGHT, PANEL_WIDTH } from "../components/ChatPanel";
import { useCanvasApi } from "../context/CanvasContext";

// Screen-space gap between icon right edge and panel left edge (px)
const ICON_SIZE = 40; // constant screen px (bubble is scaled to this)
const ICON_GAP = 10;
// Panel is placed ICON_SIZE + ICON_GAP screen-px to the right of the node origin.
// In flow coordinates at current zoom, that offset = pixels / zoom.

type ChatSessionNodeRFProps = CanvasNodeProps<
  CanvasChatSessionNodeData,
  "chatSession"
>;

type ChatSessionNodeProps = ChatSessionNodeRFProps & {
  isExpanded?: boolean;
  onToggleExpanded?: (expanded: boolean) => void;
  canvasId?: string;
};

export const ChatSessionNode = memo(function ChatSessionNode({
  id,
  data,
  positionAbsoluteX,
  positionAbsoluteY,
  selected,
  isExpanded = false,
  onToggleExpanded,
  canvasId,
}: ChatSessionNodeProps) {
  const { zoom } = useViewport();
  const canvas = useCanvasApi();

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onToggleExpanded?.(!isExpanded);
    },
    [isExpanded, onToggleExpanded],
  );

  const handleDelete = useCallback(() => {
    if (id) canvas.removeNode(id);
    onToggleExpanded?.(false);
  }, [id, canvas, onToggleExpanded]);

  // Snapshot of the node's flow position at the moment header drag starts.
  // Captured synchronously at mousedown so it's never stale.
  const dragStartPosRef = useRef({ x: 0, y: 0 });

  // Keep zoom in a ref so handlePanelDrag doesn't need it in its deps
  // (zoom can change mid-drag but we want consistent conversion during a drag).
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;

  const handlePanelDragStart = useCallback(() => {
    dragStartPosRef.current = { x: positionAbsoluteX, y: positionAbsoluteY };
  }, [positionAbsoluteX, positionAbsoluteY]);

  // Receives CUMULATIVE (dx, dy) from the drag-start point in screen space.
  // Applies them relative to the captured start position and snaps to the
  // same 25x25 grid that ReactFlow uses for native node dragging.
  const SNAP = 25;
  const handlePanelDrag = useCallback(
    (cumulativeDx: number, cumulativeDy: number) => {
      if (!id) return;
      const rawX = dragStartPosRef.current.x + cumulativeDx / zoomRef.current;
      const rawY = dragStartPosRef.current.y + cumulativeDy / zoomRef.current;
      canvas.updateNodePosition(id, {
        x: Math.round(rawX / SNAP) * SNAP,
        y: Math.round(rawY / SNAP) * SNAP,
      });
    },
    [id, canvas],
  );

  const inverseScale = 1 / zoom;
  const { flowSessionId } = data;

  const { data: flowSession } = useFlowSessionById(flowSessionId, {
    enabled: !!flowSessionId,
  });
  const sessionLabel =
    flowSession?.name?.trim() || data.firstMessage?.trim() || "Quick Chat";

  const statusDot = useMemo(() => {
    const s = flowSession?.status as string | undefined;
    if (s === "processing")
      return { className: "bg-lc-warning animate-pulse", title: "Streaming…" };
    return null; // idle — hide the dot
  }, [flowSession?.status]);

  // Panel position in flow coordinates.
  // We want the panel's left edge to be (ICON_SIZE + ICON_GAP) screen-px right of the
  // node origin. In flow space, screen pixels = flow pixels * zoom, so:
  //   flow offset = screen offset / zoom
  const panelFlowLeft = positionAbsoluteX + (ICON_SIZE + ICON_GAP) / zoom;
  const panelFlowTop = positionAbsoluteY;

  return (
    <>
      {/* Bubble icon — constant 40×40 screen size via inverse-zoom scale */}
      <div
        className={cn(
          "cursor-pointer select-none",
          "transition-all duration-150 origin-top-left",
          "hover:brightness-110 active:scale-95",
        )}
        style={{ transform: `scale(${inverseScale})` }}
        onClick={handleClick}
        title={sessionLabel}
      >
        <div
          className={cn(
            // Design-system chat bubble: rust circle, paper glyph, elev-4.
            "relative flex items-center justify-center",
            "w-10 h-10 rounded-full shadow-elev-4",
            "transition-all duration-200",
            isExpanded || selected
              ? "bg-rust ring-2 ring-canvas-selection/60"
              : "bg-rust hover:bg-[color-mix(in_oklab,var(--rust)_85%,var(--ink))]",
          )}
        >
          <MessageSquare className="h-5 w-5 text-paper" strokeWidth={2} />
          {statusDot && (
            <div
              title={statusDot.title}
              className={cn(
                "absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full border-2 border-neutral-0 dark:border-neutral-900",
                statusDot.className,
              )}
            />
          )}
        </div>

        {!isExpanded && (
          <div
            className={cn(
              "absolute top-full left-1/2 mt-2 -translate-x-1/2",
              "max-w-[180px]",
              "text-xs text-foreground font-medium truncate whitespace-nowrap",
              "pointer-events-none",
            )}
          >
            {sessionLabel}
          </div>
        )}
      </div>

      {/* Panel — inside ViewportPortal so it lives in canvas space.
          This means it automatically follows pan/zoom and icon drags.
          Two-layer approach:
            outer div  → flow-space positioning + zoom compensation (no animation)
            inner motion.div → enter/exit animation (scale + opacity spring)
          Keeping them separate avoids conflicts between Motion's transform and
          the zoom-compensation transform on the same element. */}
      <ViewportPortal>
        <AnimatePresence>
          {isExpanded && flowSessionId && canvasId && (
            // Outer motion.div: tracked by AnimatePresence (handles opacity exit)
            // + zoom-compensation transform. Keeping zoom-compensation here means
            // the inner motion.div's scale/y operate in screen-space coordinates.
            <motion.div
              key={flowSessionId}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ type: "spring", stiffness: 380, damping: 30 }}
              style={{
                position: "absolute",
                left: panelFlowLeft,
                top: panelFlowTop,
                transform: `scale(${inverseScale})`,
                transformOrigin: "top left",
                width: PANEL_WIDTH,
                height: PANEL_HEIGHT,
                zIndex: 9999,
                pointerEvents: "auto",
              }}
            >
              {/* Inner motion.div: scale + y spring — no transform conflict */}
              <motion.div
                initial={{ scale: 0.92, y: -6 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.92, y: -6 }}
                transition={{ type: "spring", stiffness: 380, damping: 30 }}
                style={{
                  transformOrigin: "top left",
                  width: "100%",
                  height: "100%",
                }}
              >
                <ChatPanel
                  flowSessionId={flowSessionId}
                  canvasId={canvasId}
                  onDragHeaderStart={handlePanelDragStart}
                  onDragHeader={handlePanelDrag}
                  onClose={() => onToggleExpanded?.(false)}
                  onDelete={handleDelete}
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </ViewportPortal>
    </>
  );
});
